import { Link, SearchableSelect, TextInputField } from "@certn/cascadia-react";
import { usePeopleAPI } from "@certn/legacy-api";
import { useField } from "formik";
import { Key, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { Item } from "react-stately";

import { fakeInstitutions } from "./constants";
import { convertToTitleCase } from "./utils";

type InstitutionsSelectProps = {
  name: string;
  label: string;
  isRequired?: boolean;
  testID?: string;
  country: string;
  province_state: string;
};

type Institution = {
  name: string;
  external_id: string;
  province_state?: string;
  province?: string;
};

export function InstitutionsSelect(props: InstitutionsSelectProps) {
  const { country, province_state, name } = props;
  const [field, , helpers] = useField(name);
  const [manualEntry, setManualEntry] = useState(
    field?.value?.name && !field?.value?.external_id
  );

  const countryValue = country;
  const stateProvinceValue = countryValue === "US" ? `${province_state}` : null;
  const isLocalEnv = window.location.hostname === "localhost";

  const { data: institutionsList } = usePeopleAPI(
    "/internal/people/applicant/institutions/",
    { country: countryValue, province_state: stateProvinceValue }
  );

  const institutionsArray = isLocalEnv
    ? (fakeInstitutions as [])
    : (institutionsList as unknown as []);

  const sortedInstitutionsList = useMemo(
    () =>
      institutionsArray
        ?.sort((a: { name: string }, b: { name: string }) =>
          a.name.localeCompare(b.name)
        )
        .map((institution: Institution) => {
          return {
            name: institution.name,
            external_id: institution.external_id,
            province: institution.province_state,
          };
        }),
    [institutionsList]
  );

  function parseKey(key: string | null) {
    if (!key) {
      return null;
    }
    const [name, external_id, province_state] = key.split(";");
    return {
      name,
      external_id,
      province_state,
    };
  }

  function generateKey(institution: Institution | null) {
    if (!institution) {
      return null;
    }
    return [
      institution.name,
      institution.external_id,
      institution.province_state,
    ].join(";");
  }

  const handleSelectionChange = (selectedValue: string) => {
    helpers.setValue(parseKey(selectedValue));
  };

  const handleManualEntryChange = (input: string) => {
    helpers.setValue({ name: input, external_id: "", province_state });
  };

  return (
    <div>
      {manualEntry ? (
        <TextInputField
          name={name}
          label={props.label}
          isRequired={props.isRequired}
          onChange={handleManualEntryChange}
          value={field.value?.name || ""}
          testID={props.testID}
        />
      ) : (
        <SearchableSelect
          name={name}
          label={props.label}
          isRequired={props.isRequired}
          onSelectionChange={(key: Key) => {
            handleSelectionChange(String(key));
          }}
          selectedKey={generateKey(field.value)}
          testID={props.testID}
        >
          {sortedInstitutionsList?.map((institution: Institution) => (
            <Item key={generateKey(institution)}>
              {convertToTitleCase(institution.name)}
            </Item>
          ))}
        </SearchableSelect>
      )}
      <Link
        textStyle="global/body-secondary"
        textColor="text-weak"
        onPress={() => setManualEntry(!manualEntry)}
        testID="switch-between-select-and-manual-input-education-button"
      >
        <Trans>{manualEntry ? "Select from list" : "Enter manually"}</Trans>
      </Link>
    </div>
  );
}
